import { Tooltip, Typography, TypographyProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useBoolean, useSize } from "ahooks";
import clsx from "clsx";
import React, { memo } from "react";

const Root = styled("span")(() => ({
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-all",
}));

//base text component

export interface ReadableTextProps extends TypographyProps {
  // content
  text: string | React.ReactNode;
  // manuel tooltips or auto tooltips
  tip?: JSX.Element | string;
  style?: React.CSSProperties;
  className?: string;
  maxWidth?: string;
  // control max rows
  maxRows?: number;
  disableTip?: boolean;
  // control tooltip interactive
  disableInteractive?: boolean;
}

export const ReadableText: React.FC<ReadableTextProps> = memo((props: any) =>  {
  const {
    disableTip,
    tip,
    text,
    maxWidth = "100%",
    maxRows = 1,
    style,
    className,
    disableInteractive,
    ...rest
  } = props;
  const cssRef = React.useRef<HTMLDivElement | null>(null);
  const nodeRef = React.useRef<HTMLDivElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const size = useSize(containerRef);
  const width = size ? size.width : 10;
  
  const [isCssEllipsis, setIsCssEllipsis] = React.useState<boolean>(false);
  const [isInit, setIsInit] = useBoolean(false);
  const measureDomRef = React.useRef<JSX.Element | null>(null);

  const init = React.useCallback(() => {
    if (!measureDomRef.current && !nodeRef.current)
      measureDomRef.current = React.createElement(
        "div",
        { ref: nodeRef, id: Math.random() },
        ""
      );
    setIsInit.setTrue();
  }, [setIsInit]);

  const clear = React.useCallback(() => {
    nodeRef.current = null;
    measureDomRef.current = null;
    setIsInit.setFalse();
  }, [setIsInit]);

  const styleToString = (style: CSSStyleDeclaration) => {
    const styleNames: string[] = Array.prototype.slice.apply(style);
    return styleNames
      .map((name: any) =>  `${name}: ${style.getPropertyValue(name)};`)
      .join("");
  };

  const setStyle = React.useCallback(
    (object: any) =>  {
      object.style.padding = "0";
      object.style.margin = "0";
      object.style.display = "inline-block";
      object.style.lineHeight = "inherit";
      object.style.position = "fixed";
      object.style.left = "0";
      object.style.minHeight = "auto";
      object.style.maxHeight = "auto";
      object.style.height = "auto";
      object.style.top = "-999999px";
      object.style.zIndex = "-999";
      object.style.textOverflow = "clip";
      object.style.whiteSpace = style?.whiteSpace ?? "normal";
      object.style.webkitLineClamp = "none";
    },
    [style]
  );

  React.useEffect(() => {
    init();
  }, [init, width]);

  React.useEffect(() => {
    if (!isInit) return;
    if (nodeRef.current && cssRef.current) {
      //append css
      const originStyle = window.getComputedStyle(cssRef.current);
      nodeRef.current.setAttribute("style", styleToString(originStyle));
      setStyle(nodeRef.current);
      nodeRef.current.innerHTML = `${text}`;

      const tolerance = 2;
      setIsCssEllipsis(
        (cssRef.current.getBoundingClientRect().height ?? 0) + tolerance <
          (nodeRef.current.getBoundingClientRect().height ?? 0)
      );
    }
    //manuel clear dom
    clear();
  }, [clear, isInit, isCssEllipsis, maxRows, setStyle, text]);

  const renderAutoTip = () =>
    isCssEllipsis ? (
      <Typography component={"span"} style={style} {...rest}>
        {text}
      </Typography>
    ) : (
      ""
    );

  const renderToolTip = () =>
    tip ? (
      <Typography component={"span"} style={style} {...rest}>
        {tip}
      </Typography>
    ) : (
      renderAutoTip()
    );

  const renderCssWidthLimitText = () => {
    return (
      <Typography component={"span"} {...rest}>
        <Tooltip
          placement={"top-start"}
          title={disableTip ? "" : renderToolTip()}
          disableInteractive={disableInteractive}
        >
          <Root
            className={clsx(className)}
            style={{
              WebkitLineClamp: maxRows ?? 1,
              display: "-webkit-box",
              whiteSpace: "normal",
              maxWidth: maxWidth,
              ...style,
            }}
            ref={cssRef}
          >
            {text}
          </Root>
        </Tooltip>
        {measureDomRef.current}
      </Typography>
    );
  };
  return (
    <div ref={containerRef} style={{ maxWidth: maxWidth, ...style }}>
      {renderCssWidthLimitText()}
    </div>
  );
});
